import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportImportModal = ({ isOpen, onClose, mode, data, dataType, onImport }) => {
  const [selectedFormats, setSelectedFormats] = useState({
    csv: true,
    json: false
  });
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleFormatToggle = (format) => {
    setSelectedFormats(prev => ({
      ...prev,
      [format]: !prev?.[format]
    }));
  };

  const handleExportCSV = () => {
    const headers = Object.keys(data?.[0] || {});
    const csvContent = [
      headers?.join(','),
      ...data?.map(row => 
        headers?.map(header => {
          const value = row?.[header];
          if (typeof value === 'string' && value?.includes(',')) {
            return `"${value}"`;
          }
          return value;
        })?.join(',')
      )
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${dataType}_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
  };

  const handleExportJSON = () => {
    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${dataType}_${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
  };

  const handleExport = () => {
    if (selectedFormats?.csv) {
      handleExportCSV();
    }
    if (selectedFormats?.json) {
      handleExportJSON();
    }
    onClose();
  };

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleImport = () => {
    if (!importFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e?.target?.result;
        let parsedData = [];

        if (importFile?.name?.endsWith('.json')) {
          parsedData = JSON.parse(content);
        } else if (importFile?.name?.endsWith('.csv')) {
          const lines = content?.split('\n')?.filter(line => line?.trim());
          const headers = lines?.[0]?.split(',')?.map(h => h?.trim());
          parsedData = lines?.slice(1)?.map((line, index) => {
            const values = line?.split(',')?.map(v => v?.trim()?.replace(/^"|"$/g, ''));
            const obj = { id: Date.now() + index };
            headers?.forEach((header, i) => {
              const value = values?.[i];
              if (!isNaN(value) && value !== '') {
                obj[header] = parseFloat(value);
              } else {
                obj[header] = value;
              }
            });
            return obj;
          });
        }

        onImport(parsedData);
        onClose();
        setImportFile(null);
      } catch (error) {
        console.error('Error parsing file:', error);
        alert('Error parsing file. Please check the file format.');
      }
    };
    reader?.readAsText(importFile);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card border border-border rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              {mode === 'export' ? 'Export Data' : 'Import Data'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {mode === 'export' ? `Export ${dataType} data` : `Import ${dataType} data from file`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          {mode === 'export' ? (
            <>
              <div>
                <h3 className="text-sm md:text-base font-semibold text-foreground mb-3">
                  Export Formats
                </h3>
                <div className="space-y-2">
                  <Checkbox
                    label="CSV Format"
                    description="Comma-separated values for Excel and spreadsheets"
                    checked={selectedFormats?.csv}
                    onChange={() => handleFormatToggle('csv')}
                  />
                  <Checkbox
                    label="JSON Format"
                    description="JavaScript Object Notation for data interchange"
                    checked={selectedFormats?.json}
                    onChange={() => handleFormatToggle('json')}
                  />
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      Export Preview
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {data?.length || 0} records will be exported with all fields included.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <h3 className="text-sm md:text-base font-semibold text-foreground mb-3">
                  Select File
                </h3>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => fileInputRef?.current?.click()}
                  fullWidth
                >
                  {importFile ? importFile?.name : 'Choose File (CSV or JSON)'}
                </Button>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      Import Guidelines
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• Ensure file format matches the data structure</li>
                      <li>• Existing records will be preserved</li>
                      <li>• New IDs will be generated for imported records</li>
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            fullWidth
            className="sm:flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            iconName={mode === 'export' ? 'Download' : 'Upload'}
            iconPosition="left"
            onClick={mode === 'export' ? handleExport : handleImport}
            disabled={mode === 'export' ? (!selectedFormats?.csv && !selectedFormats?.json) : !importFile}
            fullWidth
            className="sm:flex-1"
          >
            {mode === 'export' ? 'Export' : 'Import'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportImportModal;